import { FormlyFieldConfig } from '@ngx-formly/core';

/**
 * Types whose clear button is delivered through `FormlyWrapperClearButton`
 * (i.e. types that render via ngx-formly/material's generic
 * `formly-wrapper-mat-form-field`, which is the only thing that consumes
 * `props.suffix`).
 *
 * Handoff "Input field: Clear (X) button interaction logic" (#20830): the X is
 * not universal. Native `select` is the only generic Formly type in the allowed
 * categories (dropdowns); plain text (`input`) and `textarea` never get one —
 * a backspace is the natural correction there. An admin can still opt a field
 * in explicitly via the 'Wisknop' addon (wrapper-config.ts), which puts 'clear'
 * in `field.wrappers` before this extension runs; this function only ever adds,
 * never removes.
 */
const CLEARABLE_TYPES = new Set(['select']);

/**
 * Types that render via ngx-formly/material's `formly-wrapper-mat-form-field`.
 * For these, any wrapper this or another extension adds makes the wrapper list
 * non-empty, which stops Formly from applying the default `form-field` wrapper
 * — so it has to be kept explicit here, also for types that no longer get the
 * clear button (unit-addon inputs, admin-opted 'Wisknop' fields).
 */
const MATERIAL_FORM_FIELD_TYPES = new Set(['input', 'textarea', 'select']);

export function clearButtonExtension(field: FormlyFieldConfig): void {
  if (!field.props || !field.type || typeof field.type !== 'string') {
    return;
  }

  // Container fields (fieldGroup) and fields without a bound control (no
  // key) don't get a clear button.
  if (field.fieldGroup || !field.key) {
    return;
  }

  if (!MATERIAL_FORM_FIELD_TYPES.has(field.type)) {
    return;
  }

  const wrappers = [...(field.wrappers || [])];

  if (CLEARABLE_TYPES.has(field.type) && !wrappers.includes('clear')) {
    // Render clear outside the Material wrapper.
    const materialWrapperIndex = wrappers.indexOf('form-field');
    wrappers.splice(materialWrapperIndex === -1 ? wrappers.length : materialWrapperIndex, 0, 'clear');
  }

  if (wrappers.length === 0) {
    // Nothing added and nothing configured: leave the field untouched so
    // Formly still applies its default `form-field` wrapper.
    return;
  }

  if (!wrappers.includes('form-field')) {
    wrappers.push('form-field');
  }

  field.wrappers = wrappers;
}
